/**
 * Baustein — ExportModal
 *
 * Das komplette Export-Modal des Design-Systems: Format-Tabs + Statistik, optionaler
 * Options-Bereich, readonly Live-Vorschau, Kopieren-/Datei-Footer. Die Seite liefert nur
 * Formate, Inhalt (content) und Dateinamen — Kopieren und Download erledigt der Baustein.
 *
 * Format-Wahl sind Tabs statt Dropdown, damit alle Formate auf einen Blick auffindbar
 * sind; „Text" ist der Formate-Konvention nach immer der Default-Tab. Der Footer trägt
 * „Als Datei speichern" (`Upload`-Icon, sekundär) und „In Zwischenablage" (primär, wechselt
 * beim Klick kurz auf „Kopiert!"). Beide Aktionen sind deaktiviert, sobald `empty` gesetzt
 * ist oder `content` leer ist.
 *
 * Konsumenten: DeckExportModal, WantlistExportModal.
 *
 * <ExportModal
 *   open={open} onOpenChange={setOpen} title="Deck exportieren"
 *   formats={[{ value: 'text', label: 'Text' }, …]}
 *   format={format} onFormatChange={setFormat}
 *   stats="73 Karten (42 verschiedene)"
 *   content={exportText} filename={(f) => `deck.${f === 'csv' ? 'csv' : 'txt'}`}
 * >
 *   <ExportModal.Option checked={x} onChange={…}>Nur fehlende Karten</ExportModal.Option>
 * </ExportModal>
 */
import React, { useEffect, useRef, useState } from 'react';
import { Copy, Check, Upload } from 'lucide-react';
import SchemaModal from './SchemaModal';
import Tabs from './Tabs';
import Textarea from './Textarea';
import './ExportModal.css';

export default function ExportModal({
  open, onOpenChange, title,
  formats = [], format, onFormatChange,
  stats, content = '', filename, empty = false,
  children,
}) {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);

  useEffect(() => {
    if (!open) setCopied(false);
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, [open]);

  const isEmpty = empty || content.trim() === '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Kopieren fehlgeschlagen:', err);
    }
  };

  const handleDownload = () => {
    const name = (typeof filename === 'function' ? filename(format) : filename) || 'export.txt';
    const mime = name.endsWith('.csv') ? 'text/csv' : 'text/plain';
    const blob = new Blob([content], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <SchemaModal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={title}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={handleDownload} disabled={isEmpty}>
            <Upload size={16} aria-hidden /> Als Datei speichern
          </button>
          <button type="button" className="btn-primary" onClick={handleCopy} disabled={isEmpty}>
            {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
            {copied ? ' Kopiert!' : ' In Zwischenablage'}
          </button>
        </>
      }
    >
      <div className="ui-export-modal__head">
        <Tabs value={format} onValueChange={onFormatChange}>
          <Tabs.List>
            {formats.map((f) => (
              <Tabs.Trigger key={f.value} value={f.value}>{f.label}</Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs>
        {stats && <span className="ui-export-modal__stats">{stats}</span>}
      </div>
      {children && <div className="ui-export-modal__options">{children}</div>}
      <Textarea readOnly rows={14} value={content} spellCheck={false} aria-label="Export-Vorschau" />
    </SchemaModal>
  );
}

ExportModal.Option = function ExportModalOption({ checked, onChange, disabled = false, children }) {
  return (
    <label className={`ui-export-modal__option${disabled ? ' ui-export-modal__option--disabled' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      {children}
    </label>
  );
};
